<script lang="ts">
    import { onMount, untrack } from 'svelte';
    import { api } from '../lib/api';
    import { envs } from '../lib/stores/environment.svelte';
    import { toast } from '../lib/stores/toast.svelte';
    import { t, i18n } from '../lib/i18n.svelte';
    import { formatDateLocale, formatRelativeLocale, STALE_THRESHOLD_DAYS } from '../lib/format';
    import type { Consumer, MissedRequest } from '../lib/types';
    import SecretEditor from '../components/SecretEditor.svelte';

    // ==================================================================
    // Missing-request ledger (t_3ce8079f): per-environment tab of
    // requested-but-missing keys. EMPTY = HEALTHY (auto-resolved on
    // secret create/update). Rows are pure metadata: dismissable.
    // "Create secret" is the recovery payoff: prefilled keyName +
    // scope recommendation (1 requester → app scope, >=2 → global).
    // ==================================================================

    let consumers = $state<Consumer[]>([]);
    let rows = $state<MissedRequest[]>([]);
    let loading = $state(false);

    let creating = $state<{
        keyName: string;
        appScopeId: string | null;
        hint: string;
    } | null>(null);

    async function load() {
        if (!envs.currentId) return;
        loading = true;
        try {
            const [c, r] = await Promise.all([
                api<Consumer[]>(`/api/consumers?environmentId=${envs.currentId}`),
                api<{ items: MissedRequest[] }>(`/api/missed-requests?environmentId=${envs.currentId}`),
            ]);
            consumers = c;
            rows = r.items;
        } catch (err: any) {
            toast.error(err.message);
        } finally {
            loading = false;
        }
    }

    $effect(() => {
        if (envs.currentId) load();
    });

    onMount(() => {
        if (!envs.currentId) envs.load().then(load);
    });

    /** A single miss from long ago is a ghost, not a signal — mark stale for dismissal. */
    function isStale(row: MissedRequest): boolean {
        const ageDays = (Date.now() - new Date(row.lastSeenAt).getTime()) / 86400000;
        return row.hitCount === 1 && ageDays >= STALE_THRESHOLD_DAYS;
    }

    /**
     * Scope-placement guidance (ext 3): pre-selection ONLY, human decides.
     * Exactly 1 requester → that app's scope; >=2 → global.
     */
    function openCreate(row: MissedRequest) {
        const hint =
            row.requesterCount === 1
                ? t('missing.hintAppScope', {
                      en: `Only requested by ${row.consumerAppName} — app-scoped recommended.`,
                      nb: `Bare etterspurt av ${row.consumerAppName} — app-omfang anbefales.`,
                  })
                : t('missing.hintGlobalScope', {
                      en: `Requested by ${row.requesterCount} apps — global scope recommended.`,
                      nb: `Etterspurt av ${row.requesterCount} apper — globalt omfang anbefales.`,
                  });
        creating = {
            keyName: row.keyName,
            appScopeId: row.requesterCount === 1 ? row.consumerAppId : null,
            hint,
        };
    }

    async function dismiss(row: MissedRequest) {
        if (!confirm(t('missing.confirmDismiss', { en: 'Dismiss this ledger row? The secret data is untouched.', nb: 'Fjern denne raden? Hemmelighetsdata berøres ikke.' }))) return;
        try {
            await api(`/api/missed-requests/${row.id}`, { method: 'DELETE' });
            toast.success(t('missing.dismissedToast', { en: 'Ledger row dismissed', nb: 'Lederad fjernet' }));
            load();
        } catch (err: any) {
            toast.error(err.message);
        }
    }
</script>

<div class="page-header">
    <h1>{t('missing.title', { en: 'Missing requests', nb: 'Manglende etterspørsler' })}</h1>
    <div class="toolbar">
        <button class="ghost" onclick={load}>{t('missing.refresh', { en: 'Refresh', nb: 'Oppdater' })}</button>
    </div>
</div>

{#if loading}
    <p class="muted">{t('missing.loading', { en: 'Loading...', nb: 'Laster...' })}</p>
{:else if !envs.currentId}
    <p class="muted">{t('missing.noEnv', { en: 'Select an environment first.', nb: 'Velg et miljø først.' })}</p>
{:else if rows.length === 0}
    <div class="empty-state">
        <div class="empty-icon">✅</div>
        <h2>{t('missing.emptyTitle', { en: 'No missing requests', nb: 'Ingen manglende etterspørsler' })}</h2>
        <p>
            {t('missing.emptyBody', {
                en: 'This ledger lists secret keys that apps asked for but the vault could not serve. An empty tab is the healthy steady state: rows appear when an authenticated app fetches a missing key, and disappear automatically once the secret is created. Rows can also be dismissed by hand — they are pure metadata and never touch secret values.',
                nb: 'Denne oversikten viser hemmelighetsnøkler som apper har etterspurt, men som hvelvet ikke kunne levere. En tom fane er den sunne tilstanden: rader dukker opp når en autentisert app henter en manglende nøkkel, og forsvinner automatisk når hemmeligheten opprettes. Rader kan også fjernes manuelt — de er bare metadata og rører aldri hemmelige verdier.',
            })}
        </p>
    </div>
{:else}
    <p class="muted intro">
        {t('missing.intro', {
            en: 'Keys requested by apps but not found in this environment. They auto-resolve when the secret is created. Empty = healthy.',
            nb: 'Nøkler etterspurt av apper, men ikke funnet i dette miljøet. De løses automatisk når hemmeligheten opprettes. Tom = sunt.',
        })}
    </p>
    <div class="ledger">
        <table>
            <thead>
                <tr>
                    <th>{t('missing.colKey', { en: 'Key', nb: 'Nøkkel' })}</th>
                    <th>{t('missing.colApp', { en: 'Requesting app', nb: 'Etterspørende app' })}</th>
                    <th>{t('missing.colHits', { en: 'Hits', nb: 'Treff' })}</th>
                    <th>{t('missing.colRequesters', { en: 'Requesters', nb: 'Etterspørrere' })}</th>
                    <th>{t('missing.colFirstSeen', { en: 'First seen', nb: 'Først sett' })}</th>
                    <th>{t('missing.colLastSeen', { en: 'Last seen', nb: 'Sist sett' })}</th>
                    <th></th>
                </tr>
            </thead>
            <tbody>
                {#each rows as row (row.id)}
                    <tr class:stale={isStale(row)}>
                        <td class="mono key">{row.keyName}</td>
                        <td>{row.consumerAppName}</td>
                        <td>
                            <span class="badge">{row.hitCount}</span>
                        </td>
                        <td>{row.requesterCount}</td>
                        <td class="dim">{formatDateLocale(row.firstSeenAt, i18n.locale)}</td>
                        <td class="dim" title={formatDateLocale(row.lastSeenAt, i18n.locale)}>
                            {formatRelativeLocale(row.lastSeenAt, i18n.locale)}
                        </td>
                        <td class="actions">
                            <button class="ghost" onclick={() => openCreate(row)}>
                                {t('missing.createSecret', { en: '+ Create secret', nb: '+ Opprett hemmelighet' })}
                            </button>
                            <button
                                class="ghost icon"
                                title={t('missing.dismiss', { en: 'Dismiss', nb: 'Fjern' })}
                                onclick={() => dismiss(row)}
                            >🗑</button>
                        </td>
                    </tr>
                {/each}
            </tbody>
        </table>
    </div>
{/if}

{#if creating}
    <SecretEditor
        secret={null}
        initialScope={{ appScopeId: creating.appScopeId, groupId: null }}
        initialKeyName={creating.keyName}
        scopeHint={creating.hint}
        consumers={consumers}
        groups={[]}
        onClose={() => (creating = null)}
        onSaved={() => {
            creating = null;
            load();
        }}
    />
{/if}

<style>
    .page-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; }
    .intro { margin-bottom: 12px; font-size: 13px; }
    .ledger {
        background: var(--bg-1);
        border: 1px solid var(--border);
        border-radius: var(--radius-lg);
        overflow-x: auto;
    }
    table { width: 100%; border-collapse: collapse; font-size: 13px; }
    th, td { text-align: left; padding: 10px 14px; border-bottom: 1px solid var(--border); white-space: nowrap; }
    th { font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; color: var(--text-3); }
    tbody tr:last-child td { border-bottom: none; }
    tbody tr:hover { background: var(--bg-2); }
    .key { font-weight: 600; }
    .mono { font-family: var(--font-mono, monospace); }
    .dim { color: var(--text-3); }
    .badge {
        background: rgba(59, 130, 246, 0.15);
        color: var(--accent);
        padding: 2px 8px;
        border-radius: 999px;
        font-size: 12px;
    }
    tr.stale td { color: var(--warning, #f59e0b); }
    tr.stale .key, tr.stale td.dim { color: var(--warning, #f59e0b); opacity: 0.85; }
    .actions { text-align: right; }
    .icon { padding: 4px 8px; }
    .empty-state {
        background: var(--bg-1);
        border: 1px solid var(--border);
        border-radius: var(--radius-lg);
        padding: 48px 32px;
        text-align: center;
        max-width: 640px;
        margin: 0 auto;
    }
    .empty-icon { font-size: 40px; margin-bottom: 12px; }
    .empty-state h2 { margin: 0 0 8px; font-size: 16px; }
    .empty-state p { margin: 0; color: var(--text-3); font-size: 13px; line-height: 1.6; }
</style>
